import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AppLayout from '../../../component/appLayout';
import { BottomData, Table, TaskDone, TaskNameLink, TaskPending } from '../styles/styles';
import dayjs from 'dayjs';
import { checkIsAllBoxChecked, isEmpty } from '../../../helpers/common';
import {
  BoxWrapper,
  Content,
  ContentHeading,
  Header,
  HeaderLeft,
  HeaderTitle,
  ItemDiv,
  LeftContent,
  TableSection
} from '../styles';
import { TaskReportDateList, TaskReportStatusList, TaskReportCSVHeader } from '../../../global/row';
import { CSVLink } from 'react-csv';
import Button from '../../../component/button';
import {
  ActivityListInterface,
  TasksReportInterface,
  UserCheckListInterface
} from '../../../interfaces/ReportInterface';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { useMobileDevice } from '../../../global/useMobile';
import { rootStore } from '../../../mobx/rootStore';
import {
  BUTTON_TYPE,
  CHIP_SIZE,
  CHIP_ICON_POSITION,
  CHIP_TYPE,
  TASK_REPORT_TIME_TYPE,
  EMPTY_STATE_SIZE
, TASK_REPORT_STATUS } from '../../../global/constants';
import { observer } from 'mobx-react-lite';
import ResponsiveHeader from '../../../component/responsiveHeader';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import Chip from '../../../component/chipComponent';
import EmptyStateV2 from '../../../component/emptyState';
import { ROUTES } from "../../../routes/urls"
interface projectListInterface {
  id: string;
  name: string;
  checked: boolean;
}

interface FilterTypeInterface {
  project: projectListInterface[];
  user: UserCheckListInterface[];
  status: ActivityListInterface[];
  dueDate: ActivityListInterface[];
}

const Tasks: React.FC = () => {
  // States
  const [filterData, setFilterData] = useState<FilterTypeInterface>({
    project: [],
    user: [],
    status: [],
    dueDate: []
  });
  // state variables
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [searchUser, setSearchUser] = useState('');
  const [searchProject, setSearchProject] = useState('');
  const [usersList, setUsersList] = useState<UserCheckListInterface[]>([]);
  const [projectList, setProjectList] = useState<projectListInterface[]>([]);
  const [statusList, setStatusList] = useState<ActivityListInterface[]>([]);
  const [dateList, setDateList] = useState<ActivityListInterface[]>([]);
  const [selectAllUser, setSelectAllUser] = useState(true);
  const [selectAllProject, setSelectAllProject] = useState(true);
  const [selectAllStatus, setSelectAllStatus] = useState(true);
  const [selectAllDate, setSelectAllDate] = useState(true);

  //Mobx store variables
  const { reportStore, projectStore, workspaceStore, taskStore } = rootStore;
  const { taskReportData, filteredTaskReportData, loading } = reportStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { projects } = projectStore;
  const { tasks } = taskStore;

  //Other variables
  const isMobile = useMobileDevice();
  const history = useHistory();

  //Filter Task By selected project
  const filterTaskByProject = useCallback((taskList: TasksReportInterface[], filterData: FilterTypeInterface) => {
    const selectedProjectIds =
      filterData.project?.filter((project) => project.checked).map((project) => project.id) || [];
    const updatedTaskList = taskList.filter((task) => selectedProjectIds.includes(task.projectId));
    return updatedTaskList;
  }, []);

  //Filter Task By selected user
  const filterTaskByUser = useCallback((taskList: TasksReportInterface[], filterData: FilterTypeInterface) => {
    const selectedUserIds = filterData.user?.filter((user) => user.checked).map((user) => user.id) || [];
    const updatedTaskList = taskList.filter((task) => {
      // Show tasks with no users if selected
      if (task.users.length === 0 && selectedUserIds.includes('no-assignee')) {
        return true;
      }
      // Show tasks that have users matching selected user IDs
      return task.users.some((assignee) => selectedUserIds.includes(assignee.id));
    });
    return updatedTaskList;
  }, []);

  //Filter Task By selected status
  const filterTaskByStatus = useCallback((taskList: TasksReportInterface[], filterData: FilterTypeInterface) => {
    const selectedStatus = filterData.status?.filter((status) => status.checked).map((status) => status.key) || [];
    const updatedTaskList = taskList.filter((task) => {
      const isPendingSelected = selectedStatus.includes(TASK_REPORT_STATUS.PENDING);
      const isCompletedSelected = selectedStatus.includes(TASK_REPORT_STATUS.COMPLETED);
      return (isPendingSelected && task.status === false) || (isCompletedSelected && task.status === true);
    });
    return updatedTaskList;
  }, []);

  //Filter Task by selected Date
  const filterTaskByDate = useCallback((taskList: TasksReportInterface[], filterData: FilterTypeInterface) => {
    const selectedDates = filterData.dueDate?.filter((date) => date.checked).map((date) => date.key) || [];
    const updatedTaskList = taskList.filter((task) => {
      const isTaskWithNoDue = selectedDates.includes(TASK_REPORT_TIME_TYPE.NODATE);
      const isFutureTask = selectedDates.includes(TASK_REPORT_TIME_TYPE.FUTURE);
      const isDueTask = selectedDates.includes(TASK_REPORT_TIME_TYPE.PAST);
      return (
        (isTaskWithNoDue && !task.dueDate) ||
        (isFutureTask && task.dueDate && task.dueDate > new Date()) ||
        (isDueTask && task.dueDate && task.dueDate < new Date())
      );
    });

    return updatedTaskList;
  }, []);

  // Handle filter changes
  const applyFilterChange = useCallback(
    (filterType: FilterTypeInterface) => {
      let filteredTaskList: TasksReportInterface[] = taskReportData;
      filteredTaskList = filterTaskByProject(filteredTaskList, filterType);
      filteredTaskList = filterTaskByUser(filteredTaskList, filterType);
      filteredTaskList = filterTaskByStatus(filteredTaskList, filterType);
      filteredTaskList = filterTaskByDate(filteredTaskList, filterType);
      reportStore.setFilteredTaskReportData(filteredTaskList);
    },
    [taskReportData, filterTaskByProject, filterTaskByUser, filterTaskByStatus, filterTaskByDate, reportStore]
  );

  //Initial load function
  const loadData = useCallback(async () => {
    const newProjectList =
      projects?.map((ele) => ({
        id: ele.id,
        name: ele.name,
        checked: true
      })) || [];

    const usersList = [
      {
        id: 'no-assignee',
        name: 'No Assignee',
        phone: '',
        profile_photo: '',
        checked: true
      },
      ...(workspace?.users?.map((ele) => ({
        id: ele.id,
        name: ele.name,
        phone: ele.phone || '',
        profile_photo: ele.profile_photo || '',
        checked: true
      })) || [])
    ];

    const statusList =
      TaskReportStatusList?.map((ele: ActivityListInterface) => ({
        ...ele,
        checked: true
      })) || [];

    const timeList =
      TaskReportDateList?.map((ele) => ({
        ...ele,
        checked: true
      })) || [];

    const filterData = {
      project: newProjectList,
      user: usersList,
      status: statusList,
      dueDate: timeList
    };

    setProjectList(newProjectList);
    setUsersList(usersList);
    setStatusList(statusList);
    setDateList(timeList);
    setFilterData(filterData);
    applyFilterChange(filterData);
  }, [applyFilterChange, projects, workspace?.users]);

  useEffect(() => {
    if (workspace?.id && projects?.length > 0 && tasks?.length > 0) {
      loadData();
    }
  }, [loadData, workspace?.id, projects?.length, tasks?.length]);

  //Handle select all check box for all dropdown
  const handleSelectAll = useCallback(
    (key: string, value: boolean) => {
      let updatedFilterData = { ...filterData };

      if (key === 'Project') {
        const updatedProjectList = projectList.map((ele) => ({ ...ele, checked: value }));
        setSelectAllProject(value);
        setProjectList(updatedProjectList);
        updatedFilterData.project = updatedProjectList;
      } else if (key === 'User') {
        const updatedUserList = usersList.map((ele) => ({ ...ele, checked: value }));
        setSelectAllUser(value);
        setOpenDropdown(null);
        setUsersList(updatedUserList);
        updatedFilterData.user = updatedUserList;
      } else if (key === 'Status') {
        const updatedStatusList = TaskReportStatusList.map((ele) => ({ ...ele, checked: value }));
        setSelectAllStatus(value);
        setOpenDropdown(null);
        setStatusList(updatedStatusList);
        updatedFilterData.status = updatedStatusList;
      } else if (key === 'DueDate') {
        const updatedDueDateList = TaskReportDateList.map((ele) => ({ ...ele, checked: value }));
        setSelectAllDate(value);
        setOpenDropdown(null);
        setDateList(updatedDueDateList);
        updatedFilterData.dueDate = updatedDueDateList;
      } else {
        return;
      }
      setFilterData(updatedFilterData);
      applyFilterChange(updatedFilterData);
    },
    [applyFilterChange, filterData, projectList, usersList]
  );

  //function to get call on status dropdown checkbox change
  const onChangeStatus = useCallback(
    (key: number, value: boolean) => {
      const updatedStatusList = statusList.map((ele) => (ele.key === key ? { ...ele, checked: value } : ele));
      const updatedFilterData = { ...filterData, status: updatedStatusList };
      setStatusList(updatedStatusList);
      setFilterData(updatedFilterData);
      applyFilterChange(updatedFilterData);
      setOpenDropdown(null);
      setSelectAllStatus(checkIsAllBoxChecked(updatedStatusList));
    },
    [applyFilterChange, filterData, statusList]
  );

  //function to get call on date dropdown checkbox change
  const onChangeDate = useCallback(
    (key: number, value: boolean) => {
      const updatedDueDateList = dateList.map((ele) => (ele.key === key ? { ...ele, checked: value } : ele));
      const updatedFilterData = { ...filterData, dueDate: updatedDueDateList };
      setDateList(updatedDueDateList);
      setFilterData(updatedFilterData);
      setOpenDropdown(null);
      setSelectAllDate(checkIsAllBoxChecked(updatedDueDateList));
      applyFilterChange(updatedFilterData);
    },
    [applyFilterChange, dateList, filterData]
  );

  //function to get call on user dropdown checkbox change
  const onChangeUser = useCallback(
    (item: string, value: boolean) => {
      const updatedUserList = usersList.map((ele) => (ele.id === item ? { ...ele, checked: value } : ele));
      const updatedFilterData = { ...filterData, user: updatedUserList };
      setUsersList(updatedUserList);
      setSelectAllUser(checkIsAllBoxChecked(updatedUserList));
      setFilterData(updatedFilterData);
      setOpenDropdown(null);
      applyFilterChange(updatedFilterData);
    },
    [applyFilterChange, filterData, usersList]
  );

  //function to get call on project dropdown checkbox change
  const onChangeProject = useCallback(
    (item: string, value: boolean) => {
      const updatedProjectList = projectList.map((ele) => (ele.id === item ? { ...ele, checked: value } : ele));
      const updatedFilterData = { ...filterData, project: updatedProjectList };
      setProjectList(updatedProjectList);
      setSelectAllProject(checkIsAllBoxChecked(updatedProjectList));
      setFilterData(updatedFilterData);
      applyFilterChange(updatedFilterData);
      setOpenDropdown(null);
    },
    [applyFilterChange, filterData, projectList]
  );

  //function call on search input change in project dropdown
  const onChangeSearchProject = useCallback((value: string) => {
    if (value) {
      setSearchProject(value);
    } else {
      setSearchProject('');
    }
  }, []);

  //function call on search input change in user dropdown
  const onChangeSearchUser = useCallback((value: string) => {
    if (value) {
      setSearchUser(value);
    } else {
      setSearchUser('');
    }
  }, []);

  //Dropdown open code
  const toggleDropdown = useCallback((dropdown: string) => {
    setOpenDropdown((prev) => (prev === dropdown ? null : dropdown));
  }, []);

  const renderUserData = useMemo(() => {
    //Filter user list based on searched user
    const searchNewText = searchUser.trim().toLowerCase();
    const words = searchNewText.split(' ').filter((word) => word.length > 0);

    const searchUserResult = searchUser
      ? usersList.filter((item) => {
          const title = item?.name.trim().toLowerCase() || '';
          return words.some((word) => title.includes(word));
        })
      : usersList;

    return (
      <BottomData>
        {isEmpty(searchUser) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            isChecked={selectAllUser}
            onClick={() => handleSelectAll('User', !selectAllUser)}
          />
        )}
        {searchUserResult?.map((item: { checked: boolean; id: string; name: string }) => {
          return (
            <DropdownItem
              key={item?.id}
              label={item?.name}
              isCheckBox={true}
              isChecked={item?.checked}
              onClick={() => onChangeUser(item?.id, !item?.checked)}
            />
          );
        })}
      </BottomData>
    );
  }, [searchUser, usersList, selectAllUser, handleSelectAll, onChangeUser]);

  const renderProjectData = useMemo(() => {
    //Filter project list based on searched project
    const searchNewText = searchProject.trim().toLowerCase();
    const words = searchNewText.split(' ').filter((word) => word.length > 0);

    const searchProjectResult = searchProject
      ? projectList.filter((item) => {
          const title = item?.name.trim().toLowerCase() || '';
          return words.some((word) => title.includes(word));
        })
      : projectList;

    return (
      <BottomData>
        {isEmpty(searchProject) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            isChecked={selectAllProject}
            onClick={() => handleSelectAll('Project', !selectAllProject)}
          />
        )}
        {searchProjectResult?.map((item) => {
          return (
            <DropdownItem
              key={item?.id}
              label={item?.name}
              isCheckBox={true}
              isChecked={item?.checked}
              onClick={() => onChangeProject(item?.id, !item?.checked)}
            />
          );
        })}
      </BottomData>
    );
  }, [searchProject, projectList, selectAllProject, handleSelectAll, onChangeProject]);

  // Prepare data for CSV export
  const exportData = useMemo(() => {
    return filteredTaskReportData?.map((item) => {
      const assignedNames = item?.users?.map((user) => user?.name).join(', ') || '';
      return {
        status: item?.status ? 'Completed' : 'Pending',
        projectName: item?.projectName || '',
        name: item?.name || '',
        assigned: assignedNames || 'No Assignee',
        dueDate: item?.dueDate ? dayjs(item?.dueDate).format('MMM DD, YYYY') : 'No Due Date'
      };
    }) || [];
  }, [filteredTaskReportData]);

  const renderFilterDropdown = useMemo(() => {
    return (
      <ContentHeading>
        <LeftContent>
          <Dropdown
            modalTitle='Project'
            onChangeSearch={onChangeSearchProject}
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Project'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={openDropdown === 'Project' ? 'dropdown-icon ' : ''}
                onClick={() => toggleDropdown('Project')}
                isActive={openDropdown === 'Project'}
              />
            }
            trigger='click'
            onOutsideClick={() => setOpenDropdown(null)}>
            <ItemDiv>{renderProjectData}</ItemDiv>
          </Dropdown>

          <Dropdown
            modalTitle='Assigned'
            onChangeSearch={onChangeSearchUser}
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Assigned'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={openDropdown === 'User' ? 'dropdown-icon ' : ''}
                onClick={() => toggleDropdown('User')}
                isActive={openDropdown === 'User'}
              />
            }
            trigger='click'
            onOutsideClick={() => setOpenDropdown(null)}>
            <ItemDiv> {renderUserData}</ItemDiv>
          </Dropdown>
          <Dropdown
            modalTitle='Status'
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Status'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={openDropdown === 'Status' ? 'dropdown-icon ' : ''}
                onClick={() => toggleDropdown('Status')}
                isActive={openDropdown === 'Status'}
              />
            }
            trigger='click'
            onOutsideClick={() => setOpenDropdown(null)}>
            <BottomData isBorderRadius>
              <DropdownItem
                label='Select All'
                isCheckBox={true}
                isChecked={selectAllStatus}
                onClick={() => handleSelectAll('Status', !selectAllStatus)}
              />
              {statusList?.map((item) => {
                return (
                  <DropdownItem
                    key={item?.key}
                    label={item?.label}
                    isCheckBox={true}
                    isChecked={item?.checked}
                    onClick={(e) => onChangeStatus(item?.key, !item?.checked)}
                  />
                );
              })}
            </BottomData>
          </Dropdown>
          <Dropdown
            modalTitle='Custom Date'
            position='right'
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Due Date'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={openDropdown === 'Date' ? 'dropdown-icon ' : ''}
                onClick={() => toggleDropdown('Date')}
                isActive={openDropdown === 'Date'}
              />
            }
            trigger='click'
            onOutsideClick={() => setOpenDropdown(null)}>
            <ItemDiv>
              <BottomData isBorderRadius>
                <DropdownItem
                  label='Select All'
                  isCheckBox={true}
                  isChecked={selectAllDate}
                  onClick={() => handleSelectAll('DueDate', !selectAllDate)}
                />
                {dateList?.map((item) => {
                  return (
                    <DropdownItem
                      key={item?.key}
                      label={item?.label}
                      isCheckBox={true}
                      isChecked={item?.checked}
                      onClick={(e) => onChangeDate(item?.key, !item?.checked)}
                    />
                  );
                })}
              </BottomData>
            </ItemDiv>
          </Dropdown>
          {filteredTaskReportData?.length > 0 && (
            <CSVLink data={exportData} headers={TaskReportCSVHeader} filename='task_report.csv'>
              <Button
                title='Export as CSV'
                iconName='download-icon'
                variant={BUTTON_TYPE.SECONDARY}
                size='small'
                iconPosition='start'
              />
            </CSVLink>
          )}
        </LeftContent>
      </ContentHeading>
    );
  }, [
    dateList,
    exportData,
    filteredTaskReportData?.length,
    handleSelectAll,
    onChangeDate,
    onChangeSearchProject,
    onChangeSearchUser,
    onChangeStatus,
    openDropdown,
    renderProjectData,
    renderUserData,
    selectAllDate,
    selectAllStatus,
    statusList,
    toggleDropdown
  ]);

  return (
    <>
      <AppLayout>
        <BoxWrapper>
          {!isMobile ? (
            <Header>
              <HeaderLeft>
                <HeaderTitle>Tasks</HeaderTitle>
              </HeaderLeft>
              {!isMobile && renderFilterDropdown}
            </Header>
          ) : (
            <ResponsiveHeader title='Tasks' noMargin={true} />
          )}
          <Content>
            {isMobile && renderFilterDropdown}

            {(filteredTaskReportData?.length === 0 || !filteredTaskReportData) && !loading && (
              <EmptyStateV2
                size={EMPTY_STATE_SIZE.MEDIUM}
                title={'This is no report data.'}
                description={'You have no report data for this filter. Search for another filter.'}
                imageUrl={'reports_empty'}
                isAlignedCenter={true}
              />
            )}
            {filteredTaskReportData?.length > 0 && (
              <TableSection>
                <Table isTasks>
                  <thead>
                    <tr>
                      <th>STATUS</th>
                      <th>PROJECT NAME</th>
                      <th>NAME</th>
                      <th>ASSIGNED</th>
                      <th>DUE DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTaskReportData?.map((item, index: number) => {
                      const assignTo = item?.users?.map((user, index: number) => {
                        if (index === 0 && user?.name) {
                          return user?.name;
                        } else if (user?.name) {
                          return ', ' + user?.name;
                        }
                        return '';
                      });
                      return (
                        <tr key={`${item?.name}-${index}`}>
                          <td>
                            {item?.status === true ? (
                              <TaskDone>COMPLETED</TaskDone>
                            ) : (
                              <TaskPending>PENDING</TaskPending>
                            )}
                          </td>
                          <td>{item?.projectName}</td>
                          <td className='task-name'>
                            <TaskNameLink
                              onClick={() =>
                                history.push(ROUTES.TASK_DETAILS.replace(':id', item?.projectId).replace(':taskId', item?.id))
                              }>
                              {item?.name}
                            </TaskNameLink>
                          </td>
                          <td>{assignTo}</td>
                          <td>{item?.dueDate ? dayjs(item?.dueDate).format('MMM DD, YYYY') : 'No Due Date'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableSection>
            )}
          </Content>
        </BoxWrapper>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};
export default observer(Tasks);
